import {
  Box,
  CircularProgress,
  Alert,
  Container,
  Stack,
  Breadcrumbs,
  Typography,
  Button,
} from '@mui/joy';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ShipmentForm } from '@/components/Pages/Shipments/ShipmentForm';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import api from '@/services/axios';
import type { ShipmentResponseDTO, QueryResponse } from '@/types';
import { PrintMenu } from '@/components/Pages/Shipments/PrintMenu';

export const Route = createFileRoute('/_protected/shipments/$id')({
  component: ShipmentDetailPage,
});

function ShipmentDetailPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const {
    data: shipmentResponse,
    isLoading,
    error,
  } = useQuery<QueryResponse<ShipmentResponseDTO>>({
    queryKey: ['shipment', id],
    queryFn: () => api.get(`/shipment/${id}`),
  });

  const { mutate: cancelShipment, isPending: isCancellingShipment } =
    useMutation({
      mutationFn: () => api.put(`/shipment/${id}/Cancel`),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['shipment'] });
        toast.success('Envío cancelado correctamente');
      },
      onError: (error: AxiosError<{ errors: { Id: string } }>) => {
        toast.error(
          error.response?.data.errors.Id || 'Error al cancelar el envío'
        );
      },
    });

  const { mutate: closeShipment, isPending: isClosingShipment } = useMutation({
    mutationFn: () => api.put(`/shipment/${id}/Close`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipment'] });
      toast.success('Envío cerrado correctamente');
    },
    onError: (error: AxiosError<{ errors: { Id: string } }>) => {
      toast.error(error.response?.data.errors.Id || 'Error al cerrar el envío');
    },
  });

  const shipment = shipmentResponse?.data?.data;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !shipment) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Alert color='danger' variant='soft'>
          Error al cargar el envío
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction='row' justifyContent='space-between' sx={{ mb: 3 }}>
        <Breadcrumbs size='sm'>
          <Link to='/shipments'>Envíos</Link>
          <Typography>{id}</Typography>
        </Breadcrumbs>
        <Stack direction='row' gap={2} alignItems='center'>
          <Button
            variant='outlined'
            color='success'
            onClick={() => closeShipment()}
            loading={isClosingShipment}
            disabled={shipment.status !== 'open'}
            size='sm'
          >
            Cerrar
          </Button>
          <Button
            variant='outlined'
            color='danger'
            onClick={() => cancelShipment()}
            loading={isCancellingShipment}
            disabled={shipment.status !== 'open'}
            size='sm'
          >
            Cancelar
          </Button>

          <PrintMenu id={id} />
        </Stack>
      </Stack>

      <ShipmentForm shipmentId={parseInt(id)} shipment={shipment} />
    </Box>
  );
}
